import { cx } from '@/lib/format';
import type { Technology } from '@/lib/types';
import { TechChip } from './TechChip';

const STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** `×3` counts projects, the ticks are a 1-10 level. The legend above the
 *  list says so, because as bare numbers they read the same. */
export function TechRow({
  tech,
  levelLabel,
  usedIn,
  variant = 'card',
}: {
  tech: Technology;
  levelLabel: string;
  usedIn: string;
  variant?: 'card' | 'plain';
}) {
  const color = tech.color ?? 'rgb(var(--c-faint))';

  return (
    <div
      style={{ '--tech': color } as React.CSSProperties}
      className={cx(
        'group/item flex items-center gap-3.5 transition-all duration-300 ease-smooth',
        variant === 'card'
          ? 'rounded-plate border border-line bg-surface p-3.5 hover:border-[var(--tech)]'
          : 'rounded-plate px-2 py-1.5 hover:bg-elevated'
      )}
    >
      <TechChip tech={tech} size={variant === 'card' ? 'md' : 'sm'} />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-medium text-ink">{tech.name}</p>
          <span className="flex shrink-0 items-baseline gap-2 font-mono text-2xs tabular text-faint">
            {tech.firstUsedYear && <span>{tech.firstUsedYear}</span>}
            {(tech.projectCount ?? 0) > 0 && (
              <span title={`${tech.projectCount} ${usedIn}`}>×{tech.projectCount}</span>
            )}
          </span>
        </div>

        <span
          className="mt-1.5 flex items-end gap-[3px]"
          role="img"
          aria-label={`${levelLabel} ${tech.level}/10`}
          title={`${levelLabel} ${tech.level}/10`}
        >
          {STEPS.map((step) => (
            <span
              key={step}
              className="h-2 w-[3px] transition-colors duration-300"
              style={{ backgroundColor: step <= tech.level ? color : 'rgb(var(--c-ink) / 0.15)' }}
            />
          ))}
        </span>
      </div>
    </div>
  );
}

/** Key for both readings, shown once above a list of rows. */
export function LevelLegend({
  levelLegend,
  countLegend,
}: {
  levelLegend: string;
  countLegend: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-7 gap-y-2 text-xs text-faint">
      <span className="inline-flex items-center gap-2">
        <span className="flex items-end gap-[3px]" aria-hidden>
          {STEPS.map((step) => (
            <i
              key={step}
              className={cx('h-2 w-[3px] not-italic', step <= 6 ? 'bg-accent' : 'bg-ink/15')}
            />
          ))}
        </span>
        {levelLegend}
      </span>
      <span>{countLegend}</span>
    </div>
  );
}
